import "./style.scss";

import { FC, useContext, useEffect, useRef, useState } from "react";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { createInvoiceIntent, createSubscriptionIntent, getStripeConfig } from "../../api";

import { Elements } from "@stripe/react-stripe-js";
import PaymentStripForm from "../PaymentStripForm";
import { UserContext } from "features/account";
import { DefaultOffres, inclutAbonnementPremiumHub } from "shared/config/pricings";

interface StripePaymentCardProps {
    title: any;
    steps?: any;
    link: any;
    setDatas: (data: any) => void;
    datas: any;
    isMonth?: boolean;
    handlePreviousStep: () => void;
    handlePaymentResult: (title: string, message: string, paymentSucceeded: boolean) => void;
}

const StripePaymentCard: FC<StripePaymentCardProps> = ({
    title,
    setDatas,
    datas,
    steps,
    link,
    isMonth,
    handlePreviousStep,
    handlePaymentResult,
}) => {
    const [stripPromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
    const [clientSecret, setClientSecret] = useState(null);
    const [error, setError] = useState<string | null>(null);
    const { user, userType } = useContext(UserContext);
    // Le contexte utilisateur se résout après coup : `datas.user` est figé au
    // premier rendu et vaut souvent null. On retombe donc sur le contexte.
    const email = datas?.user?.email ?? user?.email ?? "";
    // ID Alerte Active et Cellule de crise SOS incluent la 1re année de Premium
    // Hub dans leur prix : ce qui est payé est donc la première facture d'un
    // ABONNEMENT, dont la part accompagnement est une ligne de plus.
    const avecAbonnement = inclutAbonnementPremiumHub(datas?.chosenPlan?.offreId);
    const isRecurring = Boolean(datas?.chosenPlan?.isRecurringPayment) || avecAbonnement;
    // L'intention ne doit être demandée qu'UNE fois : pour un abonnement, chaque
    // appel crée un objet Stripe.
    const requestedRef = useRef(false);
    useEffect(() => {
        getStripeConfig().then((value) => {
            setStripePromise(loadStripe(value ?? '', { locale: "fr" }));
        });
    }, []);

    useEffect(() => {
        if (requestedRef.current) return;

        const body = new FormData();
        body.append("type", userType || "individual");

        if (isRecurring) {
            // Abonnement : c'est la première facture de l'abonnement qui est
            // encaissée, pas un PaymentIntent nu. Le débit est donc immédiat et
            // facturé, et l'échéance suivante tombe à la même date l'an prochain.
            if (!email) return; // on attend que le contexte utilisateur soit résolu
            requestedRef.current = true;

            body.append("email", email);
            body.append("firstname", datas?.user?.firstname ?? user?.firstname ?? "");
            body.append("lastname", datas?.user?.lastname ?? user?.lastname ?? "");
            // Pour un accompagnement, l'abonnement souscrit est le Premium Hub ;
            // l'offre choisie par le client voyage à part, elle deviendra sa
            // seconde offre et le libellé de son paiement.
            body.append("offreName", avecAbonnement ? DefaultOffres.PREMIUM_HUB : datas?.chosenPlan?.title);
            body.append("isPerMonth", `${avecAbonnement ? false : isMonth !== false}`);
            if (avecAbonnement) {
                body.append("accompagnementOffreId", `${datas?.chosenPlan?.offreId}`);
                body.append("accompagnementTitle", `${datas?.chosenPlan?.title}`);
                if (Array.isArray(datas?.options) && datas.options.length) {
                    body.append("options", datas.options.join("|"));
                }
            }
            // Transmis à la metadata de l'abonnement : c'est ce que le webhook
            // `invoice.paid` lira pour activer la bonne offre, y compris si le
            // navigateur a disparu entre-temps.
            if (datas?.chosenPlan?.offreId != null) {
                body.append("offreId", `${datas.chosenPlan.offreId}`);
            }

            createSubscriptionIntent(body).then((value) => {
                if (value?.clientSecret) {
                    setClientSecret(value.clientSecret);
                } else {
                    requestedRef.current = false;
                    setError(value?.error ?? "La souscription n'a pas pu être préparée. Merci de réessayer dans un instant.");
                }
            });
            return;
        }

        // Paiement unique : une facture est émise côté serveur, on en confirme
        // le PaymentIntent. Le montant vient du catalogue Stripe pour les offres
        // qui y figurent ; `amount` ne sert que de repli pour les autres.
        if (!email) return; // la facture est nominative : on attend l'utilisateur
        requestedRef.current = true;

        body.append("email", email);
        body.append("firstname", datas?.user?.firstname ?? user?.firstname ?? "");
        body.append("lastname", datas?.user?.lastname ?? user?.lastname ?? "");
        body.append("head", datas?.chosenPlan?.head ?? "");
        body.append("title", datas?.chosenPlan?.title ?? title);
        body.append("amount", isMonth != false ? datas.chosenPlan?.price : datas.chosenPlan?.priceYear);
        // L'offre pilote côté serveur l'ouverture éventuelle de l'abonnement
        // différé (« puis 79 €/an dès la 2e année »).
        if (datas?.chosenPlan?.offreId != null) {
            body.append("offreId", `${datas.chosenPlan.offreId}`);
        }
        // Options cochées : elles deviennent des lignes de la MÊME facture, donc
        // un seul débit. Le serveur refuse une option qu'il ne connaît pas.
        if (Array.isArray(datas?.options) && datas.options.length) {
            body.append("options", datas.options.join("|"));
        }
        if (datas?.chosenPlan?.creditNumber != null) {
            body.append("credits", `${datas.chosenPlan.creditNumber}`);
        }
        if (isMonth == true || isMonth == false) {
            body.append("description", `${title}. Paiement  ${isMonth ? "mensuel" : "annuel"}`);
        } else {
            // is null
            body.append("description", `${title}`);
        }

        createInvoiceIntent(body).then((value) => {
            if (value?.clientSecret) {
                setClientSecret(value.clientSecret);
            } else {
                requestedRef.current = false;
                setError(value?.error ?? "Le paiement n'a pas pu être préparé. Merci de réessayer dans un instant.");
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, isRecurring]);

    return (
        <div className="StripePaymentCard d-flex flex-column align-items-center justify-content-center">
            {stripPromise && clientSecret && (
                <Elements
                    stripe={stripPromise}
                    options={{ clientSecret }}>
                    <PaymentStripForm
                        link={link}
                        title={title}
                        setDatas={setDatas}
                        datas={datas}
                        steps={steps}
                        handlePaymentResult={handlePaymentResult}
                        handlePreviousStep={handlePreviousStep}
                        isMonth={isMonth}
                    />
                </Elements>
            )}

            {error && <div className="text-danger text-center">{error}</div>}

            {!error && (!stripPromise || (!clientSecret && <div>Un instant...</div>))}
        </div>
    );
};

export default StripePaymentCard;
